
import React, { useState } from 'react';
import { SocialAccount, SocialPlatform, Language } from '../types';
import { TRANSLATIONS, getPlatformLabel } from '../translations';
import { X, Link as LinkIcon, Twitter, Youtube, Instagram, Facebook, Globe, CheckCircle, Loader2 } from 'lucide-react';

interface PlatformConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (account: SocialAccount) => void;
  language: Language;
}

const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case SocialPlatform.TWITTER: return <Twitter size={20} className="text-white" />;
      case SocialPlatform.YOUTUBE: return <Youtube size={20} className="text-white" />;
      case SocialPlatform.FACEBOOK: return <Facebook size={20} className="text-white" />;
      default: return <Globe size={20} className="text-white" />;
    }
};

const getPlatformColor = (platform: SocialPlatform) => {
    switch (platform) {
      case SocialPlatform.TWITTER: return 'bg-sky-500 hover:bg-sky-600';
      case SocialPlatform.YOUTUBE: return 'bg-red-600 hover:bg-red-700';
      case SocialPlatform.INSTAGRAM: return 'bg-pink-600 hover:bg-pink-700';
      case SocialPlatform.FACEBOOK: return 'bg-blue-600 hover:bg-blue-700';
      case SocialPlatform.TIKTOK: return 'bg-black hover:bg-slate-900 border border-slate-700';
      default: return 'bg-indigo-600 hover:bg-indigo-700';
    }
};

export const PlatformConnectModal: React.FC<PlatformConnectModalProps> = ({ isOpen, onClose, onConnect, language }) => {
  const t = TRANSLATIONS[language];
  
  // Step: 'select' -> 'verifying' -> 'verified'
  const [step, setStep] = useState<'select' | 'verifying' | 'verified'>('select');
  
  const [platform, setPlatform] = useState<SocialPlatform>(SocialPlatform.TWITTER);
  
  // Fetched Data Simulation
  const [fetchedData, setFetchedData] = useState<{ handle: string, followers: number } | null>(null);

  const handleStartVerification = () => {
    setStep('verifying');
    
    // Simulate API Call delay
    setTimeout(() => {
        // Mock data response based on platform
        const randomFollowers = Math.floor(Math.random() * 500000) + 1000;
        setFetchedData({
            handle: `@demo_${platform.toLowerCase()}_user`,
            followers: randomFollowers
        });
        setStep('verified');
    }, 2000);
  };

  const handleConfirmConnect = () => {
    if (fetchedData) {
        onConnect({
            platform: platform,
            handle: fetchedData.handle,
            url: `https://${platform.toLowerCase()}.com/${fetchedData.handle}`,
            followers: fetchedData.followers,
            verified: true
        });
        handleClose();
    }
  };

  const handleClose = () => {
      onClose();
      // Reset state after animation clears
      setTimeout(() => {
          setStep('select');
          setFetchedData(null);
          setPlatform(SocialPlatform.TWITTER);
      }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-6 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white">
            {t.connectPlatformTitle}
          </h2>
          <button onClick={handleClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
            {/* Step 1: Select Platform */}
            {step === 'select' && (
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">{t.selectPlatform}</label>
                        <select 
                            value={platform}
                            onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        >
                            {Object.values(SocialPlatform).map(p => (
                                <option key={p} value={p}>{getPlatformLabel(p, language)}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 text-sm text-slate-400">
                        <p>{t.verifyAccount} {getPlatformLabel(platform, language)}. We will redirect you to {platform} to verify ownership.</p>
                    </div>

                    <button 
                        onClick={handleStartVerification}
                        className={`w-full py-3 rounded-lg font-bold shadow-lg text-white flex items-center justify-center gap-2 transition-all ${getPlatformColor(platform)}`}
                    >
                        {getPlatformIcon(platform)}
                        {t.startVerification} {getPlatformLabel(platform, language)}
                    </button>
                </div>
            )}

            {/* Step 2: Verifying */}
            {step === 'verifying' && (
                <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <Loader2 size={48} className="text-indigo-500 animate-spin" />
                    <p className="text-white font-medium text-lg">{t.verifying}</p>
                    <p className="text-slate-400 text-sm">{t.fetchData} {getPlatformLabel(platform, language)}...</p>
                </div>
            )}

            {/* Step 3: Verified & Confirm */}
            {step === 'verified' && fetchedData && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                     <div className="flex flex-col items-center justify-center py-4">
                        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-3">
                            <CheckCircle size={32} className="text-emerald-500" />
                        </div>
                        <h3 className="text-xl font-bold text-white">{t.verifiedSuccess}</h3>
                     </div>

                     <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getPlatformColor(platform)}`}>
                             {getPlatformIcon(platform)}
                        </div>
                        <div>
                             <p className="text-white font-bold text-lg">{fetchedData.handle}</p>
                             <p className="text-slate-400 text-sm">{fetchedData.followers.toLocaleString()} Followers</p>
                        </div>
                     </div>

                     <div className="flex gap-3">
                         <button 
                            onClick={handleClose}
                            className="flex-1 py-3 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
                         >
                            {t.cancel}
                         </button>
                         <button 
                            onClick={handleConfirmConnect}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-bold shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
                         >
                            <LinkIcon size={18} /> {t.connect}
                         </button>
                     </div>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};
